if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtons = this.querySelectorAll('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        this.initializeVariantId();
      }

      initializeVariantId() {
        if (this.variantIdInput?.value) return;

        const selectedVariantScript =
          this.querySelector('[data-product-variant]') ||
          this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
        if (!selectedVariantScript) return;

        try {
          const selectedVariant = JSON.parse(selectedVariantScript.textContent);
          if (selectedVariant?.id) this.variantIdInput.value = selectedVariant.id;
        } catch {
          // The variant picker will populate the input after the next option change.
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.activeSubmitButton = evt.submitter || this.submitButton;
        if (this.activeSubmitButton.getAttribute('aria-disabled') === 'true' || this.activeSubmitButton.disabled) return;
        const buyNow = this.activeSubmitButton.hasAttribute('data-veyra-buy-now');

        this.handleErrorMessage();

        this.submitButtons.forEach((button) => button.setAttribute('aria-disabled', true));
        this.activeSubmitButton.classList.add('loading');
        this.activeSubmitButton.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        let variantId = formData.get('id');
        if (!variantId) {
          const selectedVariantScript =
            this.querySelector('[data-product-variant]') ||
            this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
          try {
            variantId = JSON.parse(selectedVariantScript?.textContent)?.id || '';
            if (variantId) formData.set('id', variantId);
          } catch {
            variantId = '';
          }
        }
        let sectionIds = [];
        if (this.cart) {
          sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
          this.cart.setActiveElement(document.activeElement);
        }

        const quantity = parseInt(formData.get('quantity')) || 1;
        const chargerVariantId = this.dataset.chargerVariantId;
        const duoVariantId = this.dataset.duoVariantId;
        const includesCharger = chargerVariantId && String(variantId) === String(duoVariantId);
        if (includesCharger) {
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify({
            items: [
              { id: Number(variantId), quantity },
              {
                id: Number(chargerVariantId),
                quantity,
                properties: { _veyra_bonus: 'Duo Eyes charger' },
              },
            ],
            sections: sectionIds,
            sections_url: window.location.pathname,
          });
        } else {
          if (sectionIds.length) {
            formData.append('sections', sectionIds);
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (buyNow) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = '/checkout';
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.activeSubmitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButtons.forEach((button) => button.removeAttribute('aria-disabled'));
            this.activeSubmitButton.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButtons.forEach((button) => button.setAttribute('disabled', 'disabled'));
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButtons.forEach((button) => button.removeAttribute('disabled'));
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
